import axios from "axios";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {
  REACT_APP_BASE_URL,
  REACT_APP_PAYMENT_URL,
  REACT_APP_PAYMENT_KEY,
  REACT_APP_WALLET_KEY,
  REACT_APP_WALLET_URL,
} from "src/helpers/constants";
import { normalizeWalletList } from "src/helpers/utils";

function normalizeToken(token?: string | null) {
  return token?.replace(/^Bearer\s+/i, "").trim() || "";
}

function isValidJwt(token?: string | null) {
  const normalized = normalizeToken(token);

  if (!normalized || normalized === "undefined" || normalized === "null") {
    return false;
  }

  try {
    jwtDecode(normalized);
    return true;
  } catch {
    return false;
  }
}

function getStoredUserAccessToken() {
  try {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      return "";
    }

    const parsedUser = JSON.parse(storedUser);
    return typeof parsedUser?.access === "string" ? parsedUser.access : "";
  } catch {
    return "";
  }
}

function persistCleanUserAccessToken(accessToken: string) {
  try {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      return;
    }

    const parsedUser = JSON.parse(storedUser);

    if (!parsedUser || typeof parsedUser !== "object") {
      return;
    }

    parsedUser.access = accessToken;
    localStorage.setItem("user", JSON.stringify(parsedUser));
  } catch {
    // Ignore malformed user payloads here. We only need the auth header to stay clean.
  }
}

async function getIdToken() {
  const axiosToken = normalizeToken((axios.defaults.headers.common as any)?.Authorization);
  const cookieToken = normalizeToken(Cookies.get("access"));
  const localToken = normalizeToken(localStorage.getItem("access"));
  const userToken = normalizeToken(getStoredUserAccessToken());

  const candidates = [axiosToken, cookieToken, localToken, userToken];
  const validToken = candidates.find((candidate) => isValidJwt(candidate)) || "";

  if (validToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${validToken}`;

    if (cookieToken && !isValidJwt(cookieToken) && cookieToken !== validToken) {
      Cookies.remove("access");
    }

    if (localToken && !isValidJwt(localToken) && localToken !== validToken) {
      localStorage.removeItem("access");
    }

    if (userToken && !isValidJwt(userToken) && userToken !== validToken) {
      persistCleanUserAccessToken("");
    }

    return validToken;
  }

  if (cookieToken && !isValidJwt(cookieToken)) {
    Cookies.remove("access");
  }

  if (localToken && !isValidJwt(localToken)) {
    localStorage.removeItem("access");
  }

  if (userToken && !isValidJwt(userToken)) {
    persistCleanUserAccessToken("");
  }

  return "";
}

function buildAuthHeaders(token: string) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

export async function defaultOptionsWithToken() {
  const idToken = await getIdToken();
  return {
    headers: buildAuthHeaders(idToken),
  };
}

export async function formDataOptionsWithToken() {
  const idToken = await getIdToken();
  return {
    headers: {
      "Content-Type": "multipart/form-data",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
  };
}

export async function paymentOptionsWithToken() {
  const idToken = await getIdToken();
  return {
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      "Hanypay-Api-Key": REACT_APP_PAYMENT_KEY,
    },
  };
}

export async function walletOptionsWithToken() {
  const idToken = await getIdToken();
  return {
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      "Hanypay-Api-Key": `${REACT_APP_WALLET_KEY}`,
    },
  };
}

export async function getUser() {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/auth/check/`, {
    ...defaultOptions,
  });
  return res.data;
}

export async function getRoles() {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/auth/roles/`, {
    ...defaultOptions,
  });
  return res.data;
}

export async function updateUser(userId: any, newUserData: any) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.patch(
    `${REACT_APP_BASE_URL}/auth/user/${userId}/`,
    {
      ...newUserData,
    },
    {
      ...defaultOptions,
    },
  );
  return res;
}

export async function getCountries() {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/common/country/`, {
    ...defaultOptions,
  });
  return res.data;
}

export async function uploadKYCDoc(userId: any, formData: any) {
  const formDataOptions = await formDataOptionsWithToken();
  const res = await axios.patch(
    `${REACT_APP_BASE_URL}/auth/user/${userId}/kyc-documents-upload/`,
    formData,
    {
      ...formDataOptions,
    },
  );
  return res;
}

export async function uploadBusinessDocs(userId: any, formData: any) {
  const formDataOptions = await formDataOptionsWithToken();
  const res = await axios.patch(`${REACT_APP_BASE_URL}/business/${userId}/upload-docs/`, formData, {
    ...formDataOptions,
  });
  return res;
}

export async function updateBusinessData(businessId: any, payload: any) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.patch(`${REACT_APP_BASE_URL}/business/${businessId}/`, payload, {
    ...defaultOptions,
  });
  return res;
}

export async function createBusiness(userId: any, payload: any) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.post(`${REACT_APP_BASE_URL}/business/${userId}/user-business/`, payload, {
    ...defaultOptions,
  });
  return res;
}

export async function setDefaultBusiness(businessId: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.patch(
    `${REACT_APP_BASE_URL}/business/${businessId}/set-default/`,
    {},
    {
      ...defaultOptions,
    },
  );
  return res.data;
}

export async function unsetDefaultBusiness(businessId: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.patch(
    `${REACT_APP_BASE_URL}/business/${businessId}/unset-default/`,
    {},
    {
      ...defaultOptions,
    },
  );
  return res.data;
}

export async function deleteBusiness(businessId: any) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.delete(`${REACT_APP_BASE_URL}/business/${businessId}/`, {
    ...defaultOptions,
  });
  return res;
}

export async function getBusinesses(userId: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/business/${userId}/user-business/`, {
    ...defaultOptions,
  });
  return res.data;
}

export async function getSelectedBusiness(businessId: any) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/business/${businessId}/`, {
    ...defaultOptions,
  });
  return res.data;
}

export async function walletTransfer(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(`${REACT_APP_PAYMENT_URL}/payments/wallet-transfers/`, payload, {
    ...paymentOptions,
  });
  return res.data;
}

export type TransferHistoryFilters = {
  status?: string;
  query?: string;
  start_date?: string;
  end_date?: string;
  transaction_type?: string;
  currency_code?: string;
  business_id?: string;
  wallet_numbers?: string;
};

export async function previewProviderCharges(provider: string, flow: string, amount: number) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/payments/preview-charges/`,
    { provider, flow, amount },
    { ...paymentOptions },
  );
  return res.data;
}

export async function requestWithdrawalOTP() {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/payments/withdrawal-otp/`,
    {},
    { ...paymentOptions },
  );
  return res.data;
}

export async function withdrawGHS(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/payments/korba-seller-ghs-disbursal/`,
    payload,
    { ...paymentOptions },
  );
  return res.data;
}

export async function withdrawKES(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/payments/synthera-seller-kes-disbursal/`,
    payload,
    { ...paymentOptions },
  );
  return res.data;
}

export async function initiateSyntheraMobileMoneyPayment(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/payments/synthera-mobile-money-raw/`,
    payload,
    {
      ...paymentOptions,
    },
  );
  return res.data;
}

export async function getTransferHistory(
  userId: string,
  page?: number,
  filters?: TransferHistoryFilters,
) {
  const paymentOptions = await paymentOptionsWithToken();
  const query: Record<string, string> = {};

  if (filters?.status && filters.status !== "all") {
    query.status = filters.status;
  }

  if (filters?.query) {
    query.query = filters.query;
  }

  if (filters?.start_date) {
    query.start_date = filters.start_date;
  }

  if (filters?.end_date) {
    query.end_date = filters.end_date;
  }

  if (filters?.transaction_type) {
    query.transaction_type = filters.transaction_type;
  }

  if (filters?.currency_code) {
    query.currency_code = filters.currency_code;
  }

  if (filters?.business_id) {
    query.business_id = filters.business_id;
  }

  if (filters?.wallet_numbers) {
    query.wallet_numbers = filters.wallet_numbers;
  }

  const res = await axios.get(`${REACT_APP_PAYMENT_URL}/payments/${userId}/transactions/?page=${page ?? 1}`, {
    ...paymentOptions,
    params: query,
  });
  return res.data;
}

export async function getAllTransferHistory(
  userId: string,
  filters?: TransferHistoryFilters,
) {
  const firstPage = await getTransferHistory(userId, 1, filters);
  const totalPages = Math.max(1, Number(firstPage?.total_pages ?? 1));

  if (totalPages === 1) {
    return firstPage;
  }

  const additionalPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      getTransferHistory(userId, index + 2, filters),
    ),
  );

  const results = [firstPage, ...additionalPages].flatMap((page) => page?.results ?? []);

  return {
    ...firstPage,
    count: results.length,
    current_page: 1,
    next: null,
    previous: null,
    results,
  };
}

export async function getUserId(walletNumber: string | number) {
  const defaultOptions = await walletOptionsWithToken();
  const res = await axios.get(`${REACT_APP_WALLET_URL}/wallets/${walletNumber}/user/`, {
    ...defaultOptions,
  });
  return res.data;
}

export async function getParticularUser(userId: number) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/auth/user/${userId}/`, {
    ...defaultOptions,
  });
  return res.data;
}

export async function getBusinessTypes() {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/business/type/`, {
    ...defaultOptions,
  });
  return res.data;
}

// Confirms who a wallet belongs to before money is sent to it. A transfer
// cannot be reversed, and a bulk run offers many chances to mistype a digit.
export async function walletNameCheck(walletNumber: string) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/payments/wallet-name-check/`,
    { wallet_number: walletNumber },
    {
      ...paymentOptions,
    },
  );
  return res.data;
}

export async function bulkTransfer(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/payments/bulk-wallet-transfers/`,
    payload,
    {
      ...paymentOptions,
    },
  );
  return res.data;
}

export async function getBusinessWallet(ownerId: string) {
  const defaultOptions = await walletOptionsWithToken();
  const res = await axios.get(`${REACT_APP_WALLET_URL}/wallets/owner/${ownerId}/`, {
    ...defaultOptions,
  });
  return normalizeWalletList(res.data);
}

export async function getP2PRequests({
  tradeType,
  currencyCode,
  paymentMethods,
  region,
  userData,
  page,
}: {
  tradeType: string;
  currencyCode: any;
  paymentMethods: string;
  region: string;
  userData?: any;
  page?: number;
}) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.get(
    `${REACT_APP_PAYMENT_URL}/trades/v1/p2p-requests/live/?page=${page ?? 1}`,
    {
      ...paymentOptions,
      params: {
        trade_type: tradeType,
        currency_code: currencyCode || userData?.currency_code,
        payment_methods: paymentMethods,
        region: region,
        page,
      },
    },
  );
  return res.data;
}

export async function getUserP2PRequests({
  tradeType,
  currencyCode,
  paymentMethods,
  region,
  page,
}: {
  tradeType: string;
  currencyCode: any;
  paymentMethods: string;
  region: string;
  page?: number;
}) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.get(
    `${REACT_APP_PAYMENT_URL}/trades/v1/p2p-requests/?page=${page ?? 1}`,
    {
      ...paymentOptions,
      params: {
        trade_type: tradeType,
        currency_code: currencyCode,
        payment_methods: paymentMethods,
        region: region,
      },
    },
  );
  return res.data;
}

export async function createTradeRequest(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(`${REACT_APP_PAYMENT_URL}/trades/v1/p2p-requests/`, payload, {
    ...paymentOptions,
  });
  return res;
}

export async function editTradeRequest(requestId: string, payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.patch(
    `${REACT_APP_PAYMENT_URL}/trades/v1/p2p-requests/${requestId}/`,
    payload,
    {
      ...paymentOptions,
    },
  );
  return res;
}

export async function currencyConversion(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(`${REACT_APP_PAYMENT_URL}/payments/exchange-rates/`, payload, {
    ...paymentOptions,
  });
  return res.data;
}

export async function checkDepositStatus(orderId: string) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/trades/v1/p2p-orders/${orderId}/check-deposit/`,
    {},
    { ...paymentOptions },
  );
  return res.data;
}

export async function createP2POrder(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(`${REACT_APP_PAYMENT_URL}/trades/v1/p2p-orders/`, payload, {
    ...paymentOptions,
  });
  return res.data;
}

export async function getP2pOrders({
  currencyCode,
  paymentMethods,
  region,
  page,
}: {
  currencyCode?: any;
  paymentMethods?: string;
  region?: string;
  page?: number;
}) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.get(`${REACT_APP_PAYMENT_URL}/trades/v1/p2p-orders/?page=${page ?? 1}`, {
    ...paymentOptions,
    params: {
      currency_code: currencyCode,
      payment_methods: paymentMethods,
      region: region,
    },
  });
  return res.data;
}

export async function verifyTradeOTP(orderId: string, otp: string) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/trades/v1/auth-codes/verify/`,
    {
      code: otp,
      order_id: orderId,
    },
    {
      ...paymentOptions,
    },
  );
  return res.data;
}

export async function createUserPaymentMethods(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(`${REACT_APP_PAYMENT_URL}/payments/payment-methods/`, payload, {
    ...paymentOptions,
  });
  return res.data;
}

export async function editUserPaymentMethods(id: string, payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.patch(
    `${REACT_APP_PAYMENT_URL}/payments/payment-methods/${id}/`,
    payload,
    {
      ...paymentOptions,
    },
  );
  return res.data;
}

export async function getUserPaymentMethods() {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.get(`${REACT_APP_PAYMENT_URL}/payments/payment-methods/`, {
    ...paymentOptions,
  });
  return res.data;
}

export async function getPaymentOptions() {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.get(`${REACT_APP_PAYMENT_URL}/payments/payment-options/`, {
    ...paymentOptions,
  });
  return res.data;
}

export async function getB2bLiveRequests({
  businessId,
  tradeType,
  currencyCode,
  paymentMethods,
  region,
  page,
}: {
  businessId: string;
  tradeType: string;
  currencyCode: any;
  paymentMethods: string;
  region: string;
  page?: number;
}) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.get(
    `${REACT_APP_PAYMENT_URL}/trades/v1/b2b-requests/live/?page=${page ?? 1}`,
    {
      ...paymentOptions,
      params: {
        business_id: businessId,
        trade_type: tradeType,
        currency_code: currencyCode,
        payment_methods: paymentMethods,
        region: region,
      },
    },
  );
  return res.data;
}

export async function getBusinessTrades({
  businessId,
  tradeType,
  currencyCode,
  paymentMethods,
  region,
  page,
}: {
  businessId: string;
  tradeType: string;
  currencyCode: any;
  paymentMethods: string;
  region: string;
  page?: number;
}) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.get(
    `${REACT_APP_PAYMENT_URL}/trades/v1/b2b-requests/?page=${page ?? 1}`,
    {
      ...paymentOptions,
      params: {
        business_id: businessId,
        trade_type: tradeType,
        currency_code: currencyCode,
        payment_methods: paymentMethods,
        region: region,
      },
    },
  );
  return res.data;
}

export async function getB2bOrders({
  businessId,
  currencyCode,
  paymentMethods,
  region,
  page,
}: {
  businessId: string;
  currencyCode?: any;
  paymentMethods?: string;
  region?: string;
  page?: number;
}) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.get(`${REACT_APP_PAYMENT_URL}/trades/v1/b2b-orders/?page=${page ?? 1}`, {
    ...paymentOptions,
    params: {
      business_id: businessId,
      currency_code: currencyCode,
      payment_methods: paymentMethods,
      region: region,
    },
  });
  return res.data;
}

export async function createBusinessTradeRequest(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(`${REACT_APP_PAYMENT_URL}/trades/v1/b2b-requests/`, payload, {
    ...paymentOptions,
  });
  return res;
}

export async function createBusinessTradeOrder(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(`${REACT_APP_PAYMENT_URL}/trades/v1/b2b-orders/`, payload, {
    ...paymentOptions,
  });
  return res.data;
}

export async function editBusinessTradeRequest(requestId: string, payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.patch(
    `${REACT_APP_PAYMENT_URL}/trades/v1/b2b-requests/${requestId}/`,
    payload,
    {
      ...paymentOptions,
    },
  );
  return res;
}

export async function confirmBusinesTradeOrder(orderId: string, businessId: string) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/trades/v1/b2b-orders/confirm/`,
    { order_id: orderId, business_id: businessId },
    {
      ...paymentOptions,
    },
  );
  return res.data;
}

export async function deleteP2pTradeRequest(id: string) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.delete(`${REACT_APP_PAYMENT_URL}/trades/v1/p2p-requests/${id}/`, {
    ...paymentOptions,
  });
  return res;
}

export async function deleteb2b2pTradeRequest(id: string) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.delete(`${REACT_APP_PAYMENT_URL}/trades/v1/b2b-requests/${id}/`, {
    ...paymentOptions,
  });
  return res;
}

export async function acceptB2bTradeOrder(payload: any) {
  const paymentOptions = await paymentOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/trades/v1/b2b-requests/accept-order/`,
    payload,
    {
      ...paymentOptions,
    },
  );
  return res.data;
}

//Teams
export async function getTeamMembers(team_id: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/teams/${team_id}/members`, {
    ...defaultOptions,
  });
  return res.data;
}

export async function createTeamMember(payload: any) {
  const defaultOptions = await defaultOptionsWithToken();
  const { email, phone, role, firstname, lastname, team } = payload;
  const res = await axios.post(
    `${REACT_APP_BASE_URL}/teams/invite-member/`,
    {
      email,
      phone,
      role,
      firstname,
      lastname,
      team,
    },
    {
      ...defaultOptions,
    },
  );
  return res;
}

export async function confirmTeamMemberDetails(team_member_id: string) {
  const res = await axios.get(
    `${REACT_APP_BASE_URL}/teams/members/${team_member_id}/check-member-exists/`,
  );
  return res.data;
}

export async function completeTeamMemberPassword(payload: any) {
  const { email, password, team_member_id, invite_token } = payload;
  const res = await axios.post(`${REACT_APP_BASE_URL}/teams/team-member/set-password/`, {
    team_member_id,
    invite_token,
    email,
    password,
  });
  return res;
}

export async function deleteTeamMember(payload: any) {
  const { team_id, team_member_id } = payload;
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.delete(
    `${REACT_APP_BASE_URL}/teams/${team_id}/members/${team_member_id}/member-details/`,
    {
      ...defaultOptions,
    },
  );
  return res;
}

export async function getPublicKey(apikey: string, businessId: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_PAYMENT_URL}/businesses/v1/get-public-key/`, {
    //...defaultOptions,
    //params: { business_id: businessId},
    headers: {
      ...(defaultOptions.headers as any),
      "Hanypay-Api-Key": apikey,
    },
  });
  return res.data;
}

// API Keys and Keypair Management
export async function createApiKey(payload: {
  business_id: string;
  name: string;
  mode: "live" | "test";
}) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_BASE_URL}/apikeys/user/${payload.business_id}/`,
    payload,
    {
      ...defaultOptions,
    },
  );
  return res;
}

export async function getApiKeys(businessId: string, page?: number, page_size?: number) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/apikeys/user/${businessId}/`, {
    ...defaultOptions,
    params: { page, page_size },
  });
  return res.data;
}

// Revoked by id, not by the key itself: only the hash is stored, so the raw
// key is unavailable from the moment it was created and the list cannot return
// it.
export async function revokeApiKey(businessId: string, keyId: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.delete(
    `${REACT_APP_BASE_URL}/apikeys/user/${businessId}/keys/${keyId}/`,
    {
      ...defaultOptions,
    },
  );
  return res.data;
}

export async function generateKeypair(apiKey: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/businesses/v1/generate-keypair/`,
    {},
    {
      //...defaultOptions,
      headers: {
        ...(defaultOptions.headers as any),
        "Hanypay-Api-Key": apiKey,
      },
    },
  );
  return res.data;
}

export async function regenerateKeypair(apiKey: string, publicKey: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.post(
    `${REACT_APP_PAYMENT_URL}/businesses/v1/regenerate-keypair/`,
    {
      public_key: publicKey,
    },
    {
      headers: {
        ...(defaultOptions.headers as any),
        "Hanypay-Api-Key": apiKey,
      },
    },
  );
  return res.data;
}

export async function getUserWallet(owner_id: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(
    `${REACT_APP_WALLET_URL}/wallets/owner/${owner_id}/`,
    {
      headers: {
        ...(defaultOptions.headers as any),
       // "Hanypay-Api-Key": apiKey,
      },
    },
  );
  return normalizeWalletList(res.data);
}

export async function getUserConsents() {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.get(`${REACT_APP_BASE_URL}/apikeys/user/consents/`, {
    ...defaultOptions,
  });
  return res.data;
}

export async function revokeUserConsent(consentId: string) {
  const defaultOptions = await defaultOptionsWithToken();
  const res = await axios.delete(`${REACT_APP_BASE_URL}/apikeys/user/consents/${consentId}/`, {
    ...defaultOptions,
  });
  return res.data;
}
